import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  SlidersHorizontal,
  Trash2,
  Upload,
  Users,
  X,
} from "lucide-react";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "src/components/ui/TableV2";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import PageSizeSelect from "src/components/ui/PageSizeSelect";
import Card from "src/components/common/card/Card";
import Popover from "src/components/ui/Popover";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import AddCustomerModal from "src/components/modals/AddCustomerModal";
import BulkImportModal, {
  BulkImportColumn,
} from "src/components/bulk-import/BulkImportModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as customerActions from "src/redux/slices/customerSlice";
import { Customer } from "src/types/customer.types";
import { toast } from "react-toastify";
import { formatAddress } from "src/utils/formatAddress";
import customerApis from "src/api/customer";

const CUSTOMER_IMPORT_COLUMNS: BulkImportColumn[] = [
  { key: "name", label: "Name", required: true, example: "Anita Sharma" },
  { key: "mobile", label: "Mobile", required: true, example: "9876543210" },
  { key: "email", label: "Email", example: "anita.sharma@example.com" },
  { key: "addressLine1", label: "Address Line 1", required: true, example: "45 MG Road" },
  { key: "addressLine2", label: "Address Line 2", example: "Near City Mall" },
  { key: "city", label: "City", example: "Pune" },
  { key: "state", label: "State", example: "Maharashtra" },
  { key: "pincode", label: "Pincode", example: "411001" },
  { key: "gstNumber", label: "GST Number", example: "" },
  { key: "notes", label: "Notes", example: "Prefers WhatsApp updates" },
];

const CustomerListPage: React.FC = () => {
  const [limit, setLimit] = useState(10);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editRow, setEditRow] = useState<Customer | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteRow, setDeleteRow] = useState<Customer | null>(null);
  const [deleteRequested, setDeleteRequested] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const { list, deleteCustomer } = useAppSelector((state) => state.customer);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canCreate =
    isTenantAdmin || (permissions?.customers || []).includes("create");
  const canEdit = isTenantAdmin || (permissions?.customers || []).includes("edit");
  const canDelete =
    isTenantAdmin || (permissions?.customers || []).includes("delete");
  const dispatch = useAppDispatch();

  const rows: Customer[] = list?.data?.data || [];

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    getList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, limit]);

  useEffect(() => {
    if (deleteCustomer.data && deleteRequested) {
      toast.success("Customer deleted successfully");
      dispatch(customerActions.resetDeleteCustomer());
      setDeleteRequested(false);
      handleDeleteModalClose();
      getList(1);
    } else if (deleteCustomer.error && deleteRequested) {
      toast.error(`Error: ${deleteCustomer.error}`);
      setDeleteRequested(false);
      handleDeleteModalClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteCustomer, dispatch, deleteRequested]);

  const getList = (page: number) =>
    dispatch(
      customerActions.getCustomersListRequest({
        page,
        limit,
        ...(search ? { search } : {}),
      })
    );

  const handlePageChange = (page: number) => getList(page);

  const handleClearFilters = () => {
    setSearchInput("");
    setSearch("");
  };
  const hasActiveFilters = !!search;

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
    setDeleteRow(null);
  };
  const onConfirmDelete = () => {
    if (!deleteRow?._id) return;
    setDeleteRequested(true);
    dispatch(customerActions.deleteCustomerRequest(deleteRow._id));
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setEditRow(null);
  };
  const handleModalSuccess = () => {
    handleModalClose();
    getList(list?.data?.pagination?.currentPage || 1);
  };

  return (
    <DashboardLayout
      meta={{
        title: "Customers | Invyntra Dashboard",
        description: "View and manage customer details",
        keywords: "customers, crm, invyntra, tenant",
      }}
    >
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <Users size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Customers
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Manage your customer directory.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {canCreate && (
              <Button
                variant="outline"
                rounded="full"
                onClick={() => setShowImportModal(true)}
              >
                <Upload size={16} className="mr-1.5" />
                Bulk import
              </Button>
            )}
            {canCreate && (
              <Button rounded="full" onClick={() => setShowAddModal(true)}>
                <Plus size={16} className="mr-1" />
                Add Customer
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card>
          <div className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              rounded="full"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? "Hide filters" : "Show filters"}
              {filtersOpen ? (
                <ChevronUp size={16} className="ml-1.5" />
              ) : (
                <ChevronDown size={16} className="ml-1.5" />
              )}
            </Button>
          </div>

          {filtersOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Search
                </label>
                <Input
                  placeholder="Search name, mobile or email..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  startAdornment={<Search size={14} />}
                />
              </div>

              <div className="flex items-end">
                <Button
                  variant="outline"
                  color="danger"
                  rounded="full"
                  fullWidth
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                >
                  <X size={15} className="mr-1.5" />
                  Clear filters
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* List */}
        <Card>
          <CardHeader
            title="Customers"
            description="Search and manage your customer directory."
            actions={<PageSizeSelect value={limit} onChange={setLimit} />}
          />

          <TableV2
            columns={[
              {
                key: "name",
                header: "Customer",
                accessor: ({ row }) => (
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {row.name}
                  </span>
                ),
              },
              { key: "mobile", header: "Mobile" },
              {
                key: "email",
                header: "Email",
                accessor: ({ row }) => row.email || "-",
              },
              {
                key: "addressLine1",
                header: "Address",
                width: "250px",
                accessor: ({ row }) => formatAddress(row) || "-",
              },
              {
                key: "actions",
                header: "Action",
                accessor: ({ row }) =>
                  !canEdit && !canDelete ? null : (
                    <Popover
                      trigger={
                        <button
                          className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                          title="Actions"
                        >
                          <MoreVertical size={16} />
                        </button>
                      }
                    >
                      {(close) => (
                        <div className="w-44 py-1.5">
                          {canEdit && (
                            <button
                              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                              onClick={() => {
                                close();
                                setEditRow(row);
                                setShowAddModal(true);
                              }}
                            >
                              <Pencil size={14} />
                              Edit
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                              onClick={() => {
                                close();
                                setDeleteRow(row);
                                setShowDeleteModal(true);
                              }}
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                    </Popover>
                  ),
              },
            ]}
            data={rows}
            pagination={list?.data?.pagination}
            onPageChange={handlePageChange}
            loading={list.loading}
          />
        </Card>
      </div>

      {showAddModal && (
        <AddCustomerModal
          open={showAddModal}
          customer={editRow}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {showImportModal && (
        <BulkImportModal
          open={showImportModal}
          onClose={() => setShowImportModal(false)}
          title="Bulk import customers"
          entityLabel="customers"
          columns={CUSTOMER_IMPORT_COLUMNS}
          sampleFileName="customer-import-sample.xlsx"
          onImport={(rows) => customerApis.bulkImport(rows)}
          onImported={() => getList(1)}
        />
      )}

      {showDeleteModal && deleteRow?._id && (
        <ConfirmationModal
          title="Delete customer"
          message={
            <>
              Are you sure you want to delete{" "}
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {deleteRow.name}
              </span>
              ? This action cannot be undone.
            </>
          }
          variant="danger"
          confirmLabel="Delete"
          onConfirm={onConfirmDelete}
          onCancel={handleDeleteModalClose}
          open={showDeleteModal}
          isLoading={deleteCustomer.loading}
        />
      )}
    </DashboardLayout>
  );
};

export default CustomerListPage;
